import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import { queryKeys } from '../lib/queryClient';
import { useAuthedQuery } from './useAuthedQuery';

// Comeback Bonus
export function useComebackBonus(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.comebackBonus,
    queryFn: async () => {
      const response = await apiClient.api.homeTasksControllerGetComebackBonuses();
      return response.bonuses.find((b) => b.canClaim) || null;
    },
    enabled,
  });
}

// Channels
export function useChannels(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.channels,
    queryFn: async () => {
      const response = await apiClient.api.homeTasksControllerGetSubscriptionChannels();
      return response.channels || [];
    },
    enabled,
  });
}

// Boost Channels
export function useBoostChannels(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.boostChannels,
    queryFn: async () => {
      const response = await apiClient.api.homeTasksControllerGetBoostChannels();
      return response.channels || [];
    },
    enabled,
  });
}

export function useCheckSubscriptions() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiClient.api.homeTasksControllerCheckSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.channels });
    },
  });
}

// Referral Bonus Progress
export function useReferralBonusProgress(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.referralProgress,
    queryFn: async () => {
      const response = await apiClient.api.homeTasksControllerGetReferralBonusProgress();
      return response.progressToBonus;
    },
    enabled,
  });
}

// Profile Tasks
export function useProfileTasks(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.profileTasks,
    queryFn: () => apiClient.api.profileTaskControllerGetStatus(),
    enabled,
  });
}

export function useActivateUsernameTask() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiClient.api.profileTaskControllerActivateUsernameTask(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profileTasks });
    },
  });
}

export function useActivateBioTask() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiClient.api.profileTaskControllerActivateBioTask(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profileTasks });
    },
  });
}

// Daily Bonus
export function useDailyBonus(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.dailyBonus,
    queryFn: async () => {
      const status = await apiClient.api.homeTasksControllerGetDailyBonusStatus();
      return {
        ...status,
        timerEndTime: status.canClaim ? null : Date.now() + status.timeUntilNextBonus,
      };
    },
    enabled,
  });
}

// Top Recent Drops
export function useTopRecentDrops(enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.topDrops,
    queryFn: async () => {
      const response = await apiClient.api.inventoryControllerGetTopRecentDrops({ limit: '40' });
      return response.items;
    },
    enabled,
  });
}

// Recent Drops
export function useRecentDrops(enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['home', 'recentDrops'] as const,
    queryFn: async () => {
      const response = await apiClient.api.inventoryControllerGetRecentInventoryItems({ limit: '20' });
      return response.items;
    },
    enabled,
  });
}

// Active Draws
export function useActiveDraws(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.draws,
    queryFn: () => apiClient.api.drawsControllerGetActiveDraws(),
    enabled,
  });
}

// Active Buff
export function useActiveBuff(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.activeBuff,
    queryFn: async () => {
      try {
        return await apiClient.api.promocodeControllerGetActivePromocodeBuff();
      } catch {
        return null;
      }
    },
    enabled,
  });
}

// Global Buff
export function useGlobalBuff(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.globalBuff,
    queryFn: async () => {
      try {
        return await apiClient.api.promocodeControllerGetGlobalPromocodeBuff();
      } catch {
        return null;
      }
    },
    enabled,
  });
}

// Home Auctions (for home screen - fetches all statuses)
export function useHomeAuctions(enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['home', 'auctions'] as const,
    queryFn: async () => {
      const response = await apiClient.api.auctionControllerGetActiveAuctions({});
      return response.auctions || [];
    },
    enabled,
    // Short stale time for auctions as they change frequently
    staleTime: 10 * 1000,
  });
}

// Daily Giveaways (public — works logged-out, `me` is null when anonymous)
export function useHomeDailyGiveaways(enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.dailyGiveaways,
    queryFn: async () => {
      const response = await apiClient.api.dailyGiveawayControllerGetDailyGiveaways();
      return response.giveaways || [];
    },
    enabled,
    // Leaderboards change frequently — short stale time like auctions.
    staleTime: 10 * 1000,
  });
}

/**
 * Весь главный экран одним запросом. До него каждый вход в `/` открывался
 * пятнадцатью параллельными GET-ами — 2.6 млн лишних запросов в сутки, и на каждый
 * своя проверка initData плюс килобайт аплинка на мобильном.
 *
 * Одиночные запросы остались: ими обновляются отдельные секции (аукционы раз в 10 с,
 * задачи раз в минуту) и ими же добираются секции, которые сервер не сумел собрать.
 */
export function useHomeSnapshot(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.homeSnapshot,
    queryFn: () => apiClient.api.homeControllerGetHome(),
    enabled,
  });
}
